import React, { useState, useEffect, useRef } from 'react';
import './StorySlideshow.css';

// Story slides data for demo - 3 key moments (moved outside component to prevent re-creation)
const STORY_SLIDES = [
  {
    id: 1,
    title: "THE MISSING HOUR",
    subtitle: "February 24, 2025 - 8:00-9:00 PM",
    content: "Ellie Ward's body is discovered in a rural field adjoining a farmhouse. The crucial 'missing hour' between her last sighting and discovery becomes the focus of the investigation.",
    image: "/story/the-missing-hour/images/story-slideshow/crime_scene_discovery.jpg",
    duration: 5000
  },
  {
    id: 2,
    title: "DETECTIVE SARAH COLLINS",
    subtitle: "Lead Investigator Takes Charge",
    content: "Detective Sarah Collins arrives at the scene and takes lead on the case. Her experience and determination will be crucial in solving this complex rural murder investigation.",
    image: "/story/the-missing-hour/images/story-slideshow/sarah_collins.jpg",
    duration: 5000
  },
  {
    id: 3,
    title: "EVIDENCE COLLECTION",
    subtitle: "Forensic Investigation Begins",
    content: "The forensic team begins collecting evidence from the crime scene. Every detail could be crucial in uncovering what happened during that mysterious missing hour.",
    image: "/story/the-missing-hour/images/story-slideshow/evidence_collection.jpg",
    duration: 5000
  }
];

const StorySlideshow = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const timerRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const dragStartYRef = useRef(null);

  const storySlides = STORY_SLIDES;

  // Auto-advance timer
  useEffect(() => {
    if (!isOpen || !storySlides || storySlides.length === 0 || isPaused) return;

    // Safety check for currentSlide bounds
    if (currentSlide < 0 || currentSlide >= storySlides.length) return;

    const slideDuration = storySlides[currentSlide].duration;
    const interval = 100; // Update progress every 100ms for more stable timing
    const increment = (interval / slideDuration) * 100;

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        
        if (newProgress >= 100) {
          // Clear timer immediately
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          
          // Advance to next slide
          if (currentSlide < storySlides.length - 1) {
            setCurrentSlide(currentSlide + 1);
          } else {
            // Story complete, close it
            onClose();
          }
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isOpen, currentSlide, storySlides, onClose, isPaused]);

  // Reset when story opens
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setProgress(0);
      setIsPaused(false);
    }
  }, [isOpen]);

  // Reset progress when slide changes
  useEffect(() => {
    setProgress(0);
  }, [currentSlide]);

  // Touch/swipe and hold handlers
  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent iOS context menu and image dragging
    touchStartRef.current = e.touches[0].clientY;
    dragStartYRef.current = e.touches[0].clientY;
    
    // Start hold timer for pause functionality
    holdTimeoutRef.current = setTimeout(() => {
      setIsPaused(true);
    }, 500); // Pause after 500ms of holding
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current || !dragStartYRef.current) return;
    
    const currentY = e.touches[0].clientY;
    const dragDistance = currentY - dragStartYRef.current;
    
    // Only track downward drags (positive dragDistance means dragging down)
    if (dragDistance > 0) {
      const maxDragDistance = 200; // Maximum drag distance before closing
      const dragPercentage = Math.min(dragDistance / maxDragDistance, 1);
      setDragProgress(dragPercentage);
    }
    
    touchEndRef.current = currentY;
    
    // Cancel hold timer if user moves (indicating swipe, not hold)
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  };

  const handleTouchEnd = () => {
    // Clear hold timer
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    
    // Check if drag was sufficient to close
    if (dragProgress > 0.5) {
      onClose();
      setDragProgress(0);
      return;
    }
    
    // Reset drag progress
    setDragProgress(0);
    
    // If it was a pause (no touch positions recorded), resume
    if (!touchStartRef.current || !touchEndRef.current) {
      if (isPaused) {
        setIsPaused(false);
      }
      return;
    }

    if (!storySlides) return;

    const touchDiff = touchStartRef.current - touchEndRef.current;
    const minSwipeDistance = 50;

    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe up - next slide
        if (currentSlide < storySlides.length - 1) {
          setCurrentSlide(prev => prev + 1);
          setProgress(0);
        }
      } else {
        // Swipe down - previous slide
        if (currentSlide > 0) {
          setCurrentSlide(prev => prev - 1);
          setProgress(0);
        }
      }
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
    dragStartYRef.current = null;
    
    // Resume if paused
    if (isPaused) {
      setIsPaused(false);
    }
  };

  // Mouse handlers for desktop
  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent any default mouse behaviors
    // Start hold timer for pause functionality (desktop)
    holdTimeoutRef.current = setTimeout(() => {
      setIsPaused(true);
    }, 500); // Pause after 500ms of holding
  };

  const handleMouseUp = (e) => {
    // Clear hold timer
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    
    // Resume if paused
    if (isPaused) {
      setIsPaused(false);
      return;
    }
  };

  // Click handler for desktop/mouse navigation
  const handleClick = (e) => {
    if (!storySlides || isPaused) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    
    // Define click zones - left half and right half
    const halfWidth = width * 0.5;
    
    if (clickX < halfWidth) {
      // Left side - go to previous slide
      if (currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
        setProgress(0);
      }
    } else {
      // Right side - go to next slide
      if (currentSlide < storySlides.length - 1) {
        setCurrentSlide(prev => prev + 1);
        setProgress(0);
      }
    }
  };

  // Manual navigation
  const goToNext = () => {
    if (storySlides && currentSlide < storySlides.length - 1) {
      setCurrentSlide(prev => prev + 1);
      setProgress(0);
    }
  };

  const goToPrevious = () => {
    if (storySlides && currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
      setProgress(0);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          goToNext();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, currentSlide, onClose]);

  if (!isOpen) return null;

  // Safety check to ensure currentSlide is within valid bounds
  if (!storySlides || storySlides.length === 0) {
    return null;
  }

  // Ensure currentSlide is within valid range
  const validCurrentSlide = Math.max(0, Math.min(currentSlide, storySlides.length - 1));
  const currentSlideData = storySlides[validCurrentSlide];

  // If there's a mismatch, update the state
  if (validCurrentSlide !== currentSlide) {
    setCurrentSlide(validCurrentSlide);
  }

  return (
    <div className="story-overlay">
      <div className="story-container">
        {/* Close button */}
        <button className="story-close-btn" onClick={onClose}>
          ×
        </button>

        {/* Progress bars */}
        <div className="story-progress-bars">
          {storySlides.map((_, index) => (
            <div key={index} className="progress-bar-container">
              <div 
                className={`progress-bar ${index === currentSlide ? 'active' : ''}`}
                style={{
                  width: index === currentSlide ? `${progress}%` : 
                         index < currentSlide ? '100%' : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* Drag to close indicator */}
        {dragProgress > 0 && (
          <div className="drag-indicator">
            <div className="drag-arrow">↓</div>
            <div className="drag-text">Drag down to close</div>
            <div className="drag-progress-bar">
              <div 
                className="drag-progress-fill"
                style={{ width: `${dragProgress * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Story content */}
        <div 
          className="story-slide"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          style={{
            transform: `translateY(${dragProgress * 50}px)`,
            opacity: 1 - (dragProgress * 0.3)
          }}
        >
          {/* Background image */}
          <div 
            className="story-background"
            style={{
              backgroundImage: `url(${currentSlideData?.image || ''})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="story-fallback-image">
              <span>📋</span>
            </div>
          </div>

          {/* Content overlay */}
          <div className="story-content">
            <div className="story-header">
              <h2 className="story-title">{currentSlideData?.title || 'Untitled'}</h2>
              <p className="story-subtitle">{currentSlideData?.subtitle || ''}</p>
            </div>
            
            <div className="story-body">
              <p className="story-text">{currentSlideData?.content || 'No content available'}</p>
            </div>

            {/* Navigation indicators */}
            <div className="story-navigation">
              <button 
                className="nav-btn prev-btn"
                onClick={goToPrevious}
                disabled={currentSlide === 0}
              >
                ←
              </button>
              
              <span className="slide-counter">
                {currentSlide + 1} / {storySlides.length}
              </span>
              
              <button 
                className="nav-btn next-btn"
                onClick={goToNext}
                disabled={currentSlide === storySlides.length - 1}
              >
                →
              </button>
            </div>
          </div>

          {/* Pause indicator */}
          {isPaused && (
            <div className="pause-indicator">
              <span>⏸</span>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default StorySlideshow;
